import React, {useState, useEffect} from 'react';
import post from '../utils/post.js';
import '../styles/mybonus.scss';

const getInviteStatus = (line) => {
	const hasPermanentMember = parseInt(line.PERNAMENTMEMBER, 10) === 1;
	const isClubMember = parseInt(line.CLUBMEMBER, 10) === 1;

	if (hasPermanentMember) {
		return {
			className: 'mybonus-page__invite-card--permanent',
			label: 'Má aktivní permici',
		};
	}

	if (isClubMember) {
		return {
			className: 'mybonus-page__invite-card--member',
			label: 'Člen bez aktivní permice',
		};
	}

	return {
		className: 'mybonus-page__invite-card--guest',
		label: 'Není člen',
	};
};

const getInviteName = (line) => {
	const nickName = line.NICKNAME !== '' ? ` (${line.NICKNAME})` : '';
	return `${line.LASTNAME} ${line.FIRSTNAME}${nickName}`;
};

// Sdílený obsah sekce Moje bonusy – používá ho stránka /mybonus(/:targetUsrId)
// i popup s pozvánkami a bonusy uživatele v monitoringu. targetUsrId = 0 → vlastní
// bonusy přihlášeného, jinak bonusy vybraného uživatele (backend to pustí jen sysadminovi).
// V popupu (variant="popup") nadpis a jméno uživatele nese hlavička popupu.
export default function MyBonusList({usrInfo, targetUsrId = 0, variant = '', onLoadingChange}) {
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState([]);
	const [dataInvited, setDataInvited] = useState([]);
	const [searchValue, setSearchValue] = useState('');
	// Offset se čte jen uvnitř loadNext (funkční setter), proto stačí setter + reset při změně uživatele.
	const [, setOffset] = useState(0);
	const [loadedCnt, setLoadedCnt] = useState(0);
	// Jméno cizího uživatele – vyplní ho backend jen když se stránka kouká na někoho jiného (sysadmin).
	const [targetUsrName, setTargetUsrName] = useState('');

	const parsedTargetUsrId = parseInt(targetUsrId, 10);
	const requestedUsrId = Number.isInteger(parsedTargetUsrId) && parsedTargetUsrId > 0 ? parsedTargetUsrId : 0;
	const isForeignUser = requestedUsrId > 0 && requestedUsrId !== (parseInt(usrInfo.usrId, 10) || 0);
	const isPopup = variant === 'popup';

	useEffect(() => {
		if (onLoadingChange) {
			onLoadingChange(loading);
		}
	}, [loading, onLoadingChange]);

	// Závislost na ID zajistí načtení i při přepnutí na jiného uživatele.
	useEffect(() => {
		setOffset(0);
		setSearchValue('');
		getDataInvited();
		getData('', 0);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [requestedUsrId]);

	// Do každého requestu přibalí cílové ID – backend ho respektuje jen sysadminovi.
	const withTargetUsr = (payload) => {
		if (requestedUsrId > 0) {
			return {...payload, USRID: requestedUsrId};
		}

		return payload;
	};

	const getDataInvited = () => {
		post('getListInvited', withTargetUsr({usrId: usrInfo.usrId}))
			.then((res) => {
				setDataInvited(res.data);
				setTargetUsrName(res.targetUsrName || '');
			})
			.catch((error) => {
				console.error(error);
			});
	};

	const loadNext = () => {
		setOffset((prevOffset) => {
			let newOffset = prevOffset + usrInfo.LIST_ROWS;
			getData(searchValue, prevOffset + usrInfo.LIST_ROWS);
			return newOffset;
		});
	};

	const getData = (search, offset) => {
		setLoading(true);

		post('getListMyBonus', withTargetUsr({search: search, offset: offset}))
			.then((res) => {
				if (offset === 0) {
					setData(res.data);
				} else {
					setData((prevRows) => {
						const updatedRows = [...prevRows, ...res.data];
						return updatedRows;
					});
				}

				setLoadedCnt(res.data.length);

				setLoading(false);
			})
			.catch((error) => {
				console.error(error);
				setLoading(false);
			});
	};

	const inviteSummary = dataInvited.reduce(
		(summary, line) => {
			const status = getInviteStatus(line);

			summary.total += 1;
			summary[status.className] += 1;

			return summary;
		},
		{
			total: 0,
			'mybonus-page__invite-card--permanent': 0,
			'mybonus-page__invite-card--member': 0,
			'mybonus-page__invite-card--guest': 0,
		},
	);

	const listInvited = dataInvited.map((line, index) => {
		const status = getInviteStatus(line);
		const hasPermanentMember = parseInt(line.PERNAMENTMEMBER, 10) === 1;

		return (
			<div className={`mybonus-page__invite-card ${status.className}`} key={`PROMO_USER${index}`}>
				<div className="mybonus-page__invite-head">
					<div className="mybonus-page__invite-person">
						<div className="mybonus-page__invite-name">{getInviteName(line)}</div>
					</div>
					<div className="mybonus-page__invite-meta">
						{hasPermanentMember ? <span className="mybonus-page__invite-date">{line.PAYEDTO_DT}</span> : ''}
						<img className={`icon2 permanent${hasPermanentMember ? ' active' : ''}`} alt="" />
					</div>
				</div>
			</div>
		);
	});

	const list = data.map((line, index) => {
		return (
			<div className="mybonus-page__bonus-row" key={index}>
				<div className="mybonus-page__bonus-meta">
					<div className="mybonus-page__bonus-user">{line.USRNAME}</div>
					<div className="mybonus-page__bonus-date">{line.CRDTS}</div>
				</div>
				<div className="mybonus-page__bonus-amount">{line.AMOUNTS}</div>
			</div>
		);
	});

	return (
		<>
			<div className="mybonus-page__hero">
				<div>
					{!isPopup ? <h1 className="mybonus-page__title">{isForeignUser ? 'Pozvánky a bonusy' : 'Moje bonusy'}</h1> : null}
					{/* U cizího uživatele (sysadmin) se místo návodu vypíše, čí data se zobrazují. */}
					{isForeignUser && !isPopup ? (
						<div className="mybonus-page__client-name">{targetUsrName}</div>
					) : null}
					<div className="mybonus-page__description">
						{isForeignUser
							? `Při koupi minimálně měsíční permice pozvaným členem se pozvajícímu připisuje bonus ${usrInfo.PROMO_BONUS} pokud je aktivní člen (Aktivní člen je člen, který má aktivní permici).`
							: `Při koupi minimálně měsíční permice tebou pozvaným členem ti bude připsán bonus ${usrInfo.PROMO_BONUS} pokud jsi aktivní člen (Aktivní člen je člen, který má aktivní permici).`}
					</div>
				</div>
				<div className="mybonus-page__summary-grid">
					<div className="mybonus-page__summary-card">
						<div className="mybonus-page__summary-label">Pozvaných</div>
						<div className="mybonus-page__summary-value">{inviteSummary.total}</div>
					</div>
					<div className="mybonus-page__summary-card mybonus-page__summary-card--permanent">
						<div className="mybonus-page__summary-label">S permicí</div>
						<div className="mybonus-page__summary-value">{inviteSummary['mybonus-page__invite-card--permanent']}</div>
					</div>
					<div className="mybonus-page__summary-card mybonus-page__summary-card--member">
						<div className="mybonus-page__summary-label">Bez permice</div>
						<div className="mybonus-page__summary-value">{inviteSummary['mybonus-page__invite-card--member']}</div>
					</div>
					<div className="mybonus-page__summary-card mybonus-page__summary-card--guest">
						<div className="mybonus-page__summary-label">Není člen</div>
						<div className="mybonus-page__summary-value">{inviteSummary['mybonus-page__invite-card--guest']}</div>
					</div>
				</div>
			</div>

			<section className="mybonus-page__section">
				<div className="mybonus-page__section-head">
					<h2 className="mybonus-page__section-title">{isForeignUser ? 'Pozvaní členové' : 'Pozval jsem'}</h2>
				</div>
				<div className="mybonus-page__invite-list">{listInvited.length > 0 ? listInvited : <div className="mybonus-page__empty">{isForeignUser ? 'Tento uživatel zatím nepozval žádného člena.' : 'Zatím jsi nepozval žádného člena.'}</div>}</div>
			</section>

			<section className="mybonus-page__section">
				<div className="mybonus-page__section-head">
					<h2 className="mybonus-page__section-title">{isForeignUser ? 'Připsané bonusy' : 'Moje připsané bonusy'}</h2>
				</div>
				<div className="mybonus-page__bonus-list">{list.length > 0 ? list : <div className="mybonus-page__empty">{isForeignUser ? 'Tento uživatel zatím nemá připsaný žádný bonus.' : 'Zatím nemáš připsaný žádný bonus.'}</div>}</div>

				<div className="mybonus-page__load-more">
					{parseInt(loadedCnt, 10) === parseInt(usrInfo.LIST_ROWS, 10) ? (
						<button className="button small loadNext mybonus-page__button" onClick={() => loadNext()}>
							Načíst další
						</button>
					) : (
						<span className="fnt_12 loadNext mybonus-page__load-all">Byly načteny všechny záznamy</span>
					)}
				</div>
			</section>
		</>
	);
}
